import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UserPlus, Plus, PencilSimple, Trash, Eye, Prohibit, UsersThree } from "@phosphor-icons/react";

const ACTIONS = ["view", "add", "edit", "delete"];
const ACTION_LABEL = { view: "View", add: "Add", edit: "Edit", delete: "Delete" };

/** Percent × 100 for storage (5.25 → 525). */
const toPercentX100 = (v) => {
  if (v === "" || v == null) return 0;
  const n = Math.round(parseFloat(v) * 100);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, 10000);
};
const fromPercentX100 = (m) => (m == null || m === "" ? "0.00" : (Number(m) / 100).toFixed(2));

const emptyForm = () => ({
  name: "",
  email: "",
  password: "",
  status: "active",
  permissions: {},
  group_ids: [],
  channels_commission: "0.00",
  ai_agent_commission: "0.00",
});

function PermMatrix({ menus, permissions, onChange }) {
  const toggle = (key, action, allowedActions) => {
    const cur = new Set(permissions[key] || []);
    if (cur.has(action)) {
      cur.delete(action);
      if (action === "view") cur.clear();
    } else {
      cur.add(action);
      if (action !== "view") cur.add("view");
    }
    const next = { ...permissions };
    const cleaned = ACTIONS.filter((a) => cur.has(a) && allowedActions.includes(a));
    if (cleaned.length) next[key] = cleaned;
    else delete next[key];
    onChange(next);
  };

  const setAll = (key, allowedActions, on) => {
    const next = { ...permissions };
    if (on) next[key] = [...allowedActions];
    else delete next[key];
    onChange(next);
  };

  const groups = {};
  for (const m of menus) {
    const g = m.group || "General";
    if (!groups[g]) groups[g] = [];
    groups[g].push(m);
  }

  return (
    <div className="space-y-4 max-h-[360px] overflow-y-auto pr-1" data-testid="perm-matrix">
      {Object.entries(groups).map(([group, items]) => (
        <div key={group}>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group}</p>
          <div className="rounded-lg border border-border divide-y divide-border">
            {items.map((m) => {
              const acts = new Set(permissions[m.key] || []);
              const allOn = m.actions.every((a) => acts.has(a));
              return (
                <div key={m.key} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                  <div className="min-w-[140px] flex-1 text-sm font-medium">{m.label}</div>
                  <div className="flex flex-wrap items-center gap-3">
                    {ACTIONS.filter((a) => m.actions.includes(a)).map((a) => (
                      <label key={a} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                        <Checkbox
                          checked={acts.has(a)}
                          onCheckedChange={() => toggle(m.key, a, m.actions)}
                          data-testid={`perm-${m.key}-${a}`}
                        />
                        {ACTION_LABEL[a]}
                      </label>
                    ))}
                    <button
                      type="button"
                      className="text-[11px] text-primary hover:underline ml-1"
                      onClick={() => setAll(m.key, m.actions, !allOn)}
                    >
                      {allOn ? "Clear" : "All"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function permSummary(perms) {
  const keys = Object.keys(perms || {});
  if (!keys.length) return "No menus";
  const n = keys.length;
  return `${n} menu${n === 1 ? "" : "s"}`;
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [menus, setMenus] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupSaving, setGroupSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, m, g] = await Promise.all([
        api.get("/admin-users"),
        api.get("/admin-users/menus"),
        api.get("/admin-users/groups"),
      ]);
      setRows(u.data || []);
      setMenus(m.data || []);
      setGroups(g.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEdit(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({
      name: row.name || "",
      email: row.email || "",
      password: "",
      status: row.status === "suspended" ? "suspended" : "active",
      permissions: { ...(row.permissions || {}) },
      group_ids: [...(row.group_ids || [])],
      channels_commission: fromPercentX100(row.channels_commission_minor),
      ai_agent_commission: fromPercentX100(row.ai_agent_commission_minor),
    });
    setOpen(true);
  };

  const toggleGroup = (gid) => {
    const set = new Set(form.group_ids || []);
    if (set.has(gid)) set.delete(gid);
    else set.add(gid);
    setForm({ ...form, group_ids: [...set] });
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (!edit && (!form.password || form.password.length < 8)) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      const channels_commission_minor = toPercentX100(form.channels_commission);
      const ai_agent_commission_minor = toPercentX100(form.ai_agent_commission);
      if (edit) {
        const body = {
          name: form.name.trim(),
          status: form.status,
          permissions: form.permissions,
          group_ids: form.group_ids,
          channels_commission_minor,
          ai_agent_commission_minor,
        };
        if (form.password) body.password = form.password;
        await api.patch(`/admin-users/${edit.id}`, body);
        toast.success("User updated");
      } else {
        await api.post("/admin-users", {
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          status: form.status,
          permissions: form.permissions,
          group_ids: form.group_ids,
          channels_commission_minor,
          ai_agent_commission_minor,
        });
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin-users/${delTarget.id}`);
      toast.success("User deleted");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const saveGroup = async () => {
    const name = groupName.trim();
    if (!name) {
      toast.error("Group name is required");
      return;
    }
    setGroupSaving(true);
    try {
      await api.post("/admin-users/groups", { name });
      toast.success("Group added");
      setGroupName("");
      setGroupOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGroupSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Create staff users, assign groups, and choose which admin menus they can view, add, edit, or delete."
      >
        <Button variant="outline" onClick={() => { setGroupName(""); setGroupOpen(true); }} data-testid="add-group-btn">
          <UsersThree className="w-4 h-4 mr-1.5" /> Add Group
        </Button>
        <Button onClick={openCreate} data-testid="users-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Add Users
        </Button>
      </PageHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={UserPlus}
          title="No staff users yet"
          description="Add a user, assign groups (Sales, Support, …), and set menu permissions."
          action={
            <Button onClick={openCreate} data-testid="users-empty-add">
              <Plus className="w-4 h-4 mr-1.5" /> Add Users
            </Button>
          }
        />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[120px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`user-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(r.groups || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        (r.groups || []).map((g) => (
                          <span
                            key={g.id}
                            className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium"
                          >
                            {g.name}
                          </span>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Eye className="w-3.5 h-3.5" />
                      {permSummary(r.permissions)}
                    </span>
                  </TableCell>
                  <TableCell><StatusPill status={r.status} /></TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`user-edit-${r.id}`}>
                        <PencilSimple className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDelTarget(r)} data-testid={`user-delete-${r.id}`}>
                        <Trash className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit user" : "Add Users"}</DialogTitle>
            <DialogDescription>
              Assign groups and menu permissions. Staff only see customers/resellers they create.
              Support group members can use backdoor login on those accounts.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="staff-name">Name</Label>
                <Input id="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="staff-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-email">Email</Label>
                <Input id="staff-email" type="email" disabled={!!edit} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-password">{edit ? "New password (optional)" : "Password"}</Label>
                <Input id="staff-password" type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="staff-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Groups</Label>
              {groups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No groups yet — use Add Group first.</p>
              ) : (
                <div className="flex flex-wrap gap-3 rounded-lg border border-border p-3" data-testid="staff-groups">
                  {groups.map((g) => (
                    <label key={g.id} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={(form.group_ids || []).includes(g.id)}
                        onCheckedChange={() => toggleGroup(g.id)}
                        data-testid={`staff-group-${g.id}`}
                      />
                      {g.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="staff-ch-comm">Channels Commission</Label>
                <div className="relative">
                  <Input
                    id="staff-ch-comm"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="pr-8"
                    value={form.channels_commission}
                    onChange={(e) => setForm({ ...form, channels_commission: e.target.value })}
                    data-testid="staff-channels-commission"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  % of the channel purchase/renewal amount (e.g. $55.65 × 20% = $11.13)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="staff-ai-comm">AI Agent / min Commission</Label>
                <div className="relative">
                  <Input
                    id="staff-ai-comm"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    className="pr-8"
                    value={form.ai_agent_commission}
                    onChange={(e) => setForm({ ...form, ai_agent_commission: e.target.value })}
                    data-testid="staff-ai-commission"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  % of the AI agent charge amount on assigned accounts
                </p>
              </div>
            </div>
            <div>
              <Label className="mb-2 block">Menu permissions</Label>
              <PermMatrix
                menus={menus}
                permissions={form.permissions}
                onChange={(permissions) => setForm({ ...form, permissions })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="staff-save-btn">
              {saving ? "Saving…" : edit ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-group-dialog">
          <DialogHeader>
            <DialogTitle>Add Group</DialogTitle>
            <DialogDescription>
              Create a staff group name. Sales and Support are included by default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="group-name">Group name</Label>
            <Input
              id="group-name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Operations"
              data-testid="group-name-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveGroup(); } }}
            />
            {groups.length > 0 && (
              <div className="pt-2">
                <p className="text-xs text-muted-foreground mb-1.5">Existing groups</p>
                <div className="flex flex-wrap gap-1.5">
                  {groups.map((g) => (
                    <span key={g.id} className="rounded-full border border-border px-2.5 py-0.5 text-xs">
                      {g.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupOpen(false)}>Cancel</Button>
            <Button onClick={saveGroup} disabled={groupSaving} data-testid="group-save-btn">
              {groupSaving ? "Saving…" : "Add group"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete user</DialogTitle>
            <DialogDescription>
              Remove <strong>{delTarget?.name}</strong> ({delTarget?.email})? They will no longer be able to sign in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doDelete} disabled={deleting} data-testid="staff-confirm-delete">
              {deleting ? "Deleting…" : <>
                <Prohibit className="w-4 h-4 mr-1.5" /> Delete
              </>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
